/**
 * État des filtres d'une liste d'exercices — sans React.
 *
 * ⚠️ **Séparé de `ExerciseFilterBar.tsx` parce que `react-refresh` interdit à un
 * module de composants d'exporter autre chose** — la règle qui a déjà valu son
 * fichier propre à `components/ui/tokens.ts`. Ce n'est pas un rangement de
 * confort : le rechargement à chaud cesse silencieusement de fonctionner sur
 * tout le fichier fautif.
 *
 * Même découpage que les `*Draft.ts` du projet : ce qui se calcule vit à côté
 * de ce qui se rend, jamais dedans.
 */

import type { ExerciseFilters } from '../../lib/localSearch';

/**
 * Ce que la barre de filtres pilote.
 *
 * ⚠️ **Un seul objet plutôt que trois `useState`** : c'est ce qui permet à la
 * bibliothèque et au sélecteur d'exercice d'une routine de partager la même
 * barre sans que leurs filtres se mettent à diverger.
 *
 * ⚠️ La **catégorie** n'y est pas, alors que `searchExercisesLocally` la
 * connaît : trois rangées de puces au-dessus d'une liste, c'est un écran de
 * formulaire, pas une bibliothèque. Elle reste atteignable en tapant son nom —
 * la recherche textuelle porte dessus.
 */
export interface ExerciseFilterState {
  favoriteOnly: boolean;
  equipment: string | null;
  muscle: string | null;
}

export const NO_EXERCISE_FILTER: ExerciseFilterState = {
  favoriteOnly: false,
  equipment: null,
  muscle: null,
};

/** Combine l'état des puces et le terme saisi en filtres de recherche. */
export function filtersOf(state: ExerciseFilterState, term: string): ExerciseFilters {
  return {
    term,
    favoriteOnly: state.favoriteOnly,
    equipment: state.equipment,
    muscle: state.muscle,
  };
}

/** Nombre de filtres actifs — sert à n'offrir « Tout effacer » que s'il y a de quoi. */
export function activeFilterCount(state: ExerciseFilterState): number {
  return (
    (state.favoriteOnly ? 1 : 0) +
    (state.equipment === null ? 0 : 1) +
    (state.muscle === null ? 0 : 1)
  );
}

/**
 * Traductions d'affichage — le jeu importé est anglophone.
 *
 * ⚠️ **Ici plutôt que dans un module de composants** : `react-refresh` interdit
 * à celui-ci d'exporter autre chose que des composants — la règle qui a déjà
 * sorti `components/ui/tokens.ts` de sa bibliothèque, puis `setLabels.ts` et
 * `setValues.ts` des écrans de séance. Ce fichier est le voisin naturel : il
 * porte déjà le vocabulaire d'affichage des exercices.
 *
 * ⚠️ **Seul `level` est traduit.** `category` et `equipment` sont un
 * vocabulaire **ouvert** (l'utilisateur crée ses exercices, §4.1) : une table
 * de traduction y serait fausse dès la première valeur qu'il saisit.
 */
export const NIVEAU_FR: Record<string, string> = {
  beginner: 'Débutant',
  intermediate: 'Intermédiaire',
  expert: 'Confirmé',
};
